// Centralized color palette for Fly Ashghabat
// Design ported from the Fly-main design system (blue #2563EB, Montserrat, light gray surfaces)
const palette = {
  // ========================================
  // BASE COLORS
  // ========================================

  // White shades
  white: "#ffffff",
  whiteAlt: "#fff",

  // Black shades
  black: "#000000",
  blackAlt: "#000",

  // Gray scale
  gray: {
    50: "#fafafa",
    100: "#f5f5f5",
    150: "#f1f5f9",
    200: "#eeeeee",
    250: "#e7e7e7",
    300: "#dfdfdf",
    350: "#d4d7d9",
    400: "#cccccc",
    425: "#c8c8c8",
    450: "#c2c2c2",
    500: "#b7b7b7",
    550: "#b0b0b0",
    600: "#9e9e9e",
    650: "#999999",
    675: "#808080",
    700: "#666666",
    750: "#64748b",
    800: "#333333",
    850: "#334155",
    900: "#1a1a2e",
    950: "#151515",
  },

  // ========================================
  // BRAND COLORS (Fly Ashghabat)
  // ========================================

  // Primary colors (Brand blue)
  primary: {
    main: "#2563EB", // Main brand blue
    dark: "#1D4ED8", // Hover
    darker: "#1E40AF", // Active states
    light: "#3B82F6", // Lighter blue
    lighter: "#60A5FA", // Even lighter
    bg: "rgba(37, 99, 235, 0.1)",
    bgLight: "rgba(37, 99, 235, 0.05)",
    shadow: "rgba(37, 99, 235, 0.35)",
    shadowHover: "rgba(37, 99, 235, 0.45)",
    shadowLight: "rgba(37, 99, 235, 0.3)",
    shadowLighter: "rgba(37, 99, 235, 0.2)",
  },

  // Secondary colors (Deep blue)
  secondary: {
    main: "#2563EB",
    dark: "#1E40AF",
    light: "#3B82F6",
    lighter: "#60A5FA",
    bg: "rgba(37, 99, 235, 0.1)",
    bgLight: "rgba(37, 99, 235, 0.05)",
    shadow: "rgba(37, 99, 235, 0.35)",
    shadowHover: "rgba(37, 99, 235, 0.45)",
  },

  // Tertiary colors (kept for compatibility — mapped to blue accents)
  tertiary: {
    main: "#2563EB",
    dark: "#1D4ED8",
    light: "#3B82F6",
    lighter: "#A1B0CC",
    cyan: "#27A7E7", // Telegram blue accent
    cyanLight: "#3b82f6",
    cyanDark: "#2563eb",
    gradient: "linear-gradient(45deg, #2563eb 0%, #3b82f6 100%)",
    bg: "rgba(37, 99, 235, 0.1)",
    bgLight: "rgba(37, 99, 235, 0.05)",
    shadow: "rgba(37, 99, 235, 0.35)",
  },

  // Accent colors
  accent: {
    orange: "#2563EB", // legacy key — accents are blue in the new design
    orangeLight: "#3B82F6",
    orangeDark: "#1D4ED8",
    teal: "#27A7E7", // Telegram blue
    tealLight: "#8DD3BB", // Mint CTA
    navy: "#2563EB",
    red: "#e63946",
    redDark: "#d00f1e",
    redLight: "#FF8682", // Coral links
  },

  // ========================================
  // SEMANTIC COLORS
  // ========================================

  // Success colors (Green)
  success: {
    main: "#43C653",
    dark: "#10b981",
    light: "#8DD3BB",
    bg: "rgba(67, 198, 83, 0.1)",
  },

  // Error/Danger colors (Red)
  error: {
    main: "#e53935",
    dark: "#dc2626",
    bright: "#ef4444",
    light: "#fef2f2",
    bg: "rgba(229, 57, 53, 0.03)",
    shadow: "rgba(229, 57, 53, 0.1)",
  },

  // Warning colors (Amber)
  warning: {
    main: "#c49000",
    dark: "#a67c00",
    light: "#ffd77a",
    gold: "#ffd700",
    beige: "#ffe4b5",
    bg: "rgba(196, 144, 0, 0.1)",
  },

  // Info colors (Blue)
  info: {
    main: "#27A7E7",
    light: "#3b82f6",
    dark: "#2563eb",
    steel: "#4682b4",
    bg: "rgba(39, 167, 231, 0.1)",
  },

  // ========================================
  // BOOKING STATUS COLORS
  // ========================================

  status: {
    initial: "#8B0000",
    paySuccess: "#FFD700",
    bookingDone: "#c49000",
    inProgressBook: "#c49000",
    ticketingDone: "#4caf50",
    certificateIssued: "#4caf50",
    ticketingFail: "#c49000",
    exchange: "#FFE4B5",
    void: "#4B0082",
    orderFail: "#c49000",
    bookingFail: "#c49000",
    payFail: "#c49000",
    bookingCancelFromClient: "#ff0302",
    bookingCancelFromAdmin: "#ff0302",
    bookingCancelError: "#c49000",
    applicationRefund: "#87CEEB",
    partialRefund: "#4682B4",
    bookingCancel: "#ff0302",
    refund: "#4682B4",
    balancePaymentRequest: "#4390ae",
    balancePaymentApproved: "#45b0b0",
  },

  // ========================================
  // UI ELEMENT COLORS
  // ========================================

  // Background colors
  background: {
    page: "#F9F9F9",
    light: "#F2F3F4",
    lighter: "#FAFCFC",
    white: "#ffffff",
    overlay: "rgba(0, 0, 0, 0.5)",
    overlayLight: "rgba(0, 0, 0, 0.35)",
    brandGradient: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
    tealGradient: "linear-gradient(45deg, #2563eb 0%, #3b82f6 100%)",
  },

  // Border colors
  border: {
    main: "rgba(37, 99, 235, 0.3)",
    light: "#E7E7E7",
    medium: "#D4D7D9",
    dark: "#A1B0CC",
    darker: "#cccccc",
    focus: "#2563EB",
    focusAlt: "#2563EB",
    error: "#e53935",
    subtle: "rgba(0, 0, 0, 0.04)",
    subtleMedium: "rgba(0, 0, 0, 0.06)",
  },

  // Shadow colors
  shadow: {
    light: "rgba(0, 0, 0, 0.06)",
    medium: "rgba(0, 0, 0, 0.1)",
    mediumPlus: "rgba(0, 0, 0, 0.12)",
    dark: "rgba(0, 0, 0, 0.15)",
    darker: "rgba(0, 0, 0, 0.2)",
    heavy: "rgba(0, 0, 0, 0.5)",
    extraHeavy: "rgba(0, 0, 0, 0.7)",
    brand: "rgba(37, 99, 235, 0.25)",
    brandHover: "rgba(37, 99, 235, 0.35)",
    teal: "rgba(39, 167, 231, 0.25)",
  },

  // Text colors
  text: {
    primary: "#2563EB", // Blue - headings & main text
    primaryDark: "#1D4ED8",
    primaryDarker: "#1E40AF",
    secondary: "#333333",
    secondaryLight: "#555555",
    muted: "#666666",
    mutedLight: "#7C8DB0",
    mutedLighter: "#AFAFC2",
    disabled: "#b7b7b7",
    white: "#ffffff",
    link: "#2563EB",
    linkHover: "#1D4ED8",
    accent: "#2563EB",
  },

  // Button colors
  button: {
    primary: "#2563EB", // Blue primary button
    primaryHover: "#1D4ED8",
    primaryActive: "#1E40AF",
    secondary: "#3B82F6",
    secondaryHover: "#2563EB",
    tertiary: "#2563EB",
    tertiaryHover: "#3B82F6",
    outline: "#ffffff",
    outlineHover: "#F2F3F4",
    disabled: "#e5e6ea",
    disabledText: "#999999",
    disabledBorder: "#b0b0b0",
  },

  // ========================================
  // HEADER COLORS
  // ========================================

  header: {
    background: "#ffffff",
    backgroundAlt: "#2563EB", // Blue header variant
    text: "#2563EB",
    textMuted: "#666666",
    textSecondary: "#7C8DB0",
    textWhite: "#ffffff",
    hoverBg: "#F2F3F4",

    // Primary accent (Blue)
    primary: "#2563EB",
    primaryDark: "#1D4ED8",
    primaryLight: "rgba(37, 99, 235, 0.1)",
    primaryShadow: "rgba(37, 99, 235, 0.35)",
    primaryShadowHover: "rgba(37, 99, 235, 0.45)",

    // Secondary accent
    secondary: "#3B82F6",
    secondaryLight: "#60A5FA",

    // Status colors
    success: "#10b981",
    danger: "#ef4444",
    dangerDark: "#dc2626",
    dangerBg: "#fef2f2",

    // Border & Shadow
    border: "rgba(37, 99, 235, 0.3)",
    shadow: "rgba(0, 0, 0, 0.06)",

    // Avatar
    avatarGradientStart: "#2563EB",
    avatarGradientEnd: "#3B82F6",
    avatarShadow: "rgba(37, 99, 235, 0.3)",
    avatarShadowHover: "rgba(37, 99, 235, 0.4)",

    // Dropdown
    dropdownBg: "#ffffff",
    dropdownBorder: "rgba(37, 99, 235, 0.3)",
    dropdownShadow: "rgba(0, 0, 0, 0.12)",
    dropdownDivider: "#F2F3F4",
    dropdownItemText: "#2563EB",

    // Mobile menu
    mobileOverlay: "rgba(0, 0, 0, 0.5)",
    mobilePanelShadow: "rgba(0, 0, 0, 0.15)",
  },

  // ========================================
  // INPUT/FORM COLORS
  // ========================================

  input: {
    border: "#A1B0CC",
    borderFocus: "#2563EB",
    borderFocusAlt: "#2563EB",
    borderError: "#e53935",
    borderSuccess: "#43C653",
    errorText: "#e53935",
    errorBg: "rgba(229, 57, 53, 0.03)",
    focusShadow: "rgba(37, 99, 235, 0.15)",
    focusShadowAlt: "rgba(37, 99, 235, 0.15)",
    errorShadow: "rgba(229, 57, 53, 0.1)",
    labelText: "#7C8DB0",
  },

  // ========================================
  // LEGACY COLORS (for backwards compatibility)
  // ========================================

  headerBg: "#2563EB", // Blue
  footerBg: "#2563EB", // Blue footer
  searchFormBg: "#ffffff", // Search form is a white card in the new design
  searchButtonBg: "#2563EB", // Blue
  removeRouteButtonBg: "#FF8682",
  hoverButtonBg: "#1D4ED8",

  // ========================================
  // FLIGHT SEARCH SPECIFIC
  // ========================================

  flight: {
    cardBg: "#ffffff",
    cardBorder: "rgba(37, 99, 235, 0.3)",
    cardHoverBorder: "#2563EB",
    cardHoverShadow: "rgba(37, 99, 235, 0.15)",
    priceText: "#2563EB",
    priceHighlight: "#1D4ED8",
    airlineLogo: "#2563EB",
    routeLine: "#C8C8C8",
    layoverBg: "#E8F0FE",
    selectedBg: "rgba(37, 99, 235, 0.05)",
    selectedBorder: "#2563EB",
  },

  // ========================================
  // FLY DESIGN SYSTEM TOKENS (new)
  // ========================================

  fly: {
    brand: "#2563EB",
    brandHover: "#1D4ED8",
    brandBorder: "rgba(37, 99, 235, 0.3)",
    pageBg: "#F9F9F9",
    cardBg: "#ffffff",
    formBg: "#FAFCFC",
    inputBg: "#F2F3F4",
    segmentBg: "#E8F0FE", // flight times block in result card
    classGroupBg: "#ECEFF5", // cabin class segmented control track
    toggleTrackBg: "#F2F2F2", // gender toggle group
    checkboxBg: "#EDF0F1",
    checkboxBorder: "#C2C2C2",
    inputBorder: "#A1B0CC",
    placeholder: "#7C8DB0",
    hairline: "#CCCCCC",
    hairlineLight: "#E7E7E7",
    divider: "#D4D7D9",
    searchIconChip: "rgba(57, 85, 130, 0.5)", // chip behind magnifier icon in search button
    whatsapp: "#43C653",
    telegram: "#27A7E7",
    mint: "#8DD3BB",
    coral: "#FF8682",
    glassGradient:
      "linear-gradient(90.12deg, rgba(234, 240, 240, 0.3) 0%, rgba(234, 240, 240, 0.2) 100%)",
    cardShadow: "0 4px 5px rgba(0, 0, 0, 0.15)",
    formShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    dropdownShadow: "0 4px 12px 4px rgba(0, 0, 0, 0.15)",
    softShadow: "0 4px 16px rgba(17, 34, 17, 0.05)",
    modalShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
  },

  // ========================================
  // BRAND SPECIFIC
  // ========================================

  brand: {
    // Main brand colors
    orange: "#2563EB", // legacy key — blue now
    navy: "#2563EB",
    teal: "#27A7E7",
    cyan: "#3b82f6",

    // Logo colors for reference
    logoOrange: "#2563EB",
    logoNavy: "#2563EB",
    logoTealDark: "#27A7E7",
    logoTealLight: "#3b82f6",

    // Gradient (for swoosh effect)
    swooshGradient: "linear-gradient(90deg, #2563EB 0%, #3B82F6 50%, #1D4ED8 100%)",
  },
};

export default palette;
